import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, take, tap, throwError } from 'rxjs';
import { AppStateResetService } from '@core/state/app-state-reset.service';
import { ApiErrorBody } from '@features/auth/models/auth.model';
import { DashboardQueryParams, DashboardResponse } from '../models/dashboard.model';
import { DashboardApiService } from './dashboard-api.service';
import { DashboardStateService } from './dashboard-state.service';

/**
 * Single entry point for the home dashboard. Pages inject this facade only -
 * never {@link DashboardApiService} or {@link DashboardStateService} directly.
 */
@Injectable({ providedIn: 'root' })
export class DashboardFacadeService {
  private readonly api = inject(DashboardApiService);
  private readonly state = inject(DashboardStateService);
  private readonly appStateReset = inject(AppStateResetService);

  constructor() {
    this.appStateReset.register(() => this.reset());
  }

  readonly panel = this.state.panel;
  readonly status = this.state.status;
  readonly error = this.state.error;
  readonly isLoading = this.state.isLoading;
  readonly isLoaded = this.state.isLoaded;
  readonly isError = this.state.isError;

  loadDashboard(params?: DashboardQueryParams): Observable<DashboardResponse> {
    this.state.setLoading();

    return this.api.getDashboard(params).pipe(
      take(1),
      tap((response) => this.state.setLoaded(response)),
      catchError((error: unknown) => this.handleError(error)),
    );
  }

  reset(): void {
    this.state.reset();
  }

  private handleError(error: unknown): Observable<never> {
    if (error instanceof HttpErrorResponse) {
      this.state.setError(this.extractErrorMessage(error, 'DASHBOARD.ERROR'));
      return throwError(() => error);
    }

    this.state.setError('DASHBOARD.ERROR');
    return throwError(() => error);
  }

  private extractErrorMessage(error: HttpErrorResponse, fallback: string): string {
    const body = error.error as ApiErrorBody | null;
    if (body?.message) {
      return body.message;
    }

    return fallback;
  }
}
